import PropTypes from "prop-types";
import { cn } from "@shared/core/lib/utils";

/**
 * Universal Typography component for consistent text styling across platforms
 * Provides semantic HTML elements with design system typography classes
 */
export const Typography = ({
  variant = "body1",
  component,
  className = "",
  children,
  ...props
}) => {
  // Typography variants with default styling
  const variants = {
    // Headings
    h1: "text-4xl font-bold leading-tight",
    h2: "text-3xl font-bold leading-tight",
    h3: "text-2xl font-semibold leading-tight",
    h4: "text-xl font-semibold leading-tight",
    h5: "text-lg font-semibold leading-tight",
    h6: "text-base font-semibold leading-tight",

    // Body text
    body1: "text-base leading-relaxed",
    body2: "text-sm leading-relaxed",

    // Specialized text
    caption: "text-xs text-gray-600 dark:text-gray-400",
    overline:
      "text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400",
    subtitle1: "text-lg font-medium",
    subtitle2: "text-base font-medium",

    // Interactive text
    button: "font-medium leading-none",
    link: "text-blue-600 dark:text-blue-400 hover:underline cursor-pointer",

    // Status text
    error: "text-red-600 dark:text-red-400",
    warning: "text-amber-600 dark:text-amber-400",
    success: "text-green-600 dark:text-green-400",
    info: "text-blue-600 dark:text-blue-400",
  };

  // Default HTML elements for each variant
  const defaultElements = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    body1: "p",
    body2: "p",
    caption: "span",
    overline: "span",
    subtitle1: "p",
    subtitle2: "p",
    button: "span",
    link: "a",
    error: "p",
    warning: "p",
    success: "p",
    info: "p",
  };

  const Component = component || defaultElements[variant] || "p";
  const variantClasses = variants[variant] || variants.body1;

  return (
    <Component className={cn(variantClasses, className)} {...props}>
      {children}
    </Component>
  );
};

Typography.propTypes = {
  variant: PropTypes.oneOf([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "body1",
    "body2",
    "caption",
    "overline",
    "subtitle1",
    "subtitle2",
    "button",
    "link",
    "error",
    "warning",
    "success",
    "info",
  ]),
  component: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

// Convenience components for common use cases
export const Heading = ({ level = 1, children, ...props }) => (
  <Typography variant={`h${level}`} {...props}>
    {children}
  </Typography>
);

export const Body = ({ size = 1, children, ...props }) => (
  <Typography variant={`body${size}`} {...props}>
    {children}
  </Typography>
);

export const Caption = ({ children, ...props }) => (
  <Typography variant="caption" {...props}>
    {children}
  </Typography>
);

export const Link = ({ children, ...props }) => (
  <Typography variant="link" component="a" {...props}>
    {children}
  </Typography>
);

// PropTypes for convenience components
Heading.propTypes = {
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  children: PropTypes.node.isRequired,
};

Body.propTypes = {
  size: PropTypes.oneOf([1, 2]),
  children: PropTypes.node.isRequired,
};

Caption.propTypes = {
  children: PropTypes.node.isRequired,
};

Link.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
};

export default Typography;
